import React, { useEffect, useState } from 'react'
import * as CronofyElements from 'cronofy-elements'
import { CronofyElementMethods } from 'cronofy-elements'
import cryptoRandomString from 'crypto-random-string'
import QueryString from 'query-string'
import { Alert, Button, Col, Row, Space, Typography } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import FillDataAlert from '../FillDataAlert'
import DisconnectProfileModal from './DisconnectProfileModal'
import SpinLoader from '../../../helpers/SpinLoader'
import { cronofyClientId, cronofyRedirectUri, currentBrand } from '../../../../config'
import { useSchedulerContext } from '../../../../context/scheduler/scheduler'
import { useAuthContext } from '../../../../context/auth'
import './index.less'
import { useAccountContext } from '../../../../context/account'
import { IAccount } from '../../../../context/types'
import { useExpertProfilesContext } from '../../../../context/expertProfile'
import CustomLink from '../../../helpers/CustomLink/CustomLink'

const { Text, Title, Paragraph } = Typography
type UrlParam = {
	accountId: string
}

function Scheduler() {
	const { code, error } = QueryString.parse(window.location.search)
	const { scheduler, actions } = useSchedulerContext()
	const { auth, actions: authActions } = useAuthContext()
	const { account } = useAccountContext()
	const { actions: expertProfilesActions } = useExpertProfilesContext()
	const history = useHistory()
	const { accountId } = useParams() as UrlParam
	const [cronofyElementMethods, setCronofyElementMethods] = useState<CronofyElementMethods | null>(null)
	const [userData, setUserData] = useState<IAccount>()

	const newState = cryptoRandomString({ length: 10, type: 'base64' })
	const cronofyAuthUrl = `https://app.cronofy.com/oauth/authorize?response_type=code&client_id=${cronofyClientId}&redirect_uri=${cronofyRedirectUri}&scope=read_write&state=${newState}&avoid_linking=true`

	useEffect(() => {
		if (account.token) {
			setUserData(account)
		} else if (!account.loading && !account.token) {
			setUserData(auth)
		}
	}, [account, auth])

	useEffect(() => {
		if (userData?.user?.expertInfo?.cronofy_account_id) {
			const token = account?.token && !!accountId ? account.token : null
			actions.getManageAvailabilityElementToken(token)
		}
	}, [userData?.user?.expertInfo?.cronofy_account_id])

	useEffect(() => {
		if (scheduler?.manageAvailabilityElementToken && !cronofyElementMethods) {
			setCronofyElementMethods(
				CronofyElements.AvailabilityRules({
					element_token: scheduler.manageAvailabilityElementToken,
					target_id: 'scheduler',
					styles: {
						colors: {
							unavailable: '#F0F0F0',
						},
					},
					tzid: auth.user.timezone,
					config: {
						duration: 30,
					},
					callback: async ({ notification, availability_rule }) => {
						if (notification.type === 'availability_rule_saved') {
							const isFilled = !!availability_rule.weekly_periods.length
							const token = account?.token && !!accountId ? account.token : null
							await authActions.cronofyFilled(isFilled, token)
							expertProfilesActions.getExpertProfiles(token)
						}
					},
				})
			)
		} else if (scheduler?.manageAvailabilityElementToken && cronofyElementMethods) {
			cronofyElementMethods.update({
				element_token: scheduler.manageAvailabilityElementToken,
				target_id: 'scheduler',
				styles: {
					colors: {
						unavailable: '#F0F0F0',
					},
				},
				tzid: auth.user.timezone,
				config: {
					duration: 30,
				},
				callback: async ({ notification, availability_rule }) => {
					if (notification.type === 'availability_rule_saved') {
						const isFilled = !!availability_rule.weekly_periods.length
						const token = account?.token && !!accountId ? account.token : null
						await authActions.cronofyFilled(isFilled, token)
						expertProfilesActions.getExpertProfiles(token)
					}
				},
			})
		}
	}, [scheduler?.manageAvailabilityElementToken])

	useEffect(() => {
		if (error) {
			actions.setError((error as string) || 'Cannot login to Cronofy! Please try again.')
		} else if (code) {
			actions.attach(code as string)
			setTimeout(() => {
				history.push({ search: '' })
			})
		}
	}, [code, error])

	const handleSaveRules = () => {
		const saveButton: HTMLButtonElement = document.querySelector('.AvailabilityViewer__submit')
		saveButton.click()
	}

	return (
		<div className="scheduler">
			<Title level={3}>My Availability</Title>
			<FillDataAlert />
			<SpinLoader loading={scheduler.loading}>
				<Paragraph strong style={{ fontSize: 16 }}>
					To show up in searches you must either set office hours OR connect a calendar.
				</Paragraph>
				<Alert
					type="info"
					showIcon
					message="Office Hours"
					style={{ marginBottom: 10 }}
					description={
						<Space direction="vertical">
							<p>
								You can set specific days/times that you would like to accept calls - we will check these rules against
								all of your connected calendars.
							</p>
						</Space>
					}
				/>
				<br />
				<div id="scheduler" />
				{!account?.token && (
					<>
						<Title level={3}>Connect calendar</Title>
						<Space wrap align="start" className="alerts_wrapper">
							<Alert
								type="info"
								showIcon
								message="Availability Guide"
								style={{ maxWidth: 556 }}
								description={
									<Space direction="vertical">
										<p>
											Connect as many personal and business calendars as you like to provide real-time availability for
											our members. Once your calendars are connected, every time you add an appointment to any connected
											calendar, it will be reflected on your profile availability
										</p>
										<Row gutter={[10, 10]}>
											<Col lg={12}>
												<Button
													type="primary"
													shape="round"
													onClick={handleSaveRules}
													href={cronofyAuthUrl}
													loading={scheduler.loading}
												>
													Link a Calendar
												</Button>
											</Col>
											<Col lg={12}>
												<DisconnectProfileModal refresh={cronofyElementMethods?.refresh} />
											</Col>
										</Row>
									</Space>
								}
							/>
							<Alert
								type="warning"
								showIcon
								message={<strong>ATTN: Outlook/365/Exchange users:</strong>}
								style={{ maxWidth: 556 }}
								description={
									<Space direction="vertical">
										<span>
											If this is a personal email -- Choose Outlook
											<br />
											If this is a company-provided outlook instance - please see instructions
										</span>
										<CustomLink
											to={currentBrand.contentLinks.outlookExchange.to}
											target={currentBrand.contentLinks.outlookExchange.target}
										>
											{currentBrand.contentLinks.outlookExchange.name}
										</CustomLink>
									</Space>
								}
							/>
						</Space>
					</>
				)}
				{!!account?.token && <Space className="alerts_wrapper"> </Space>}
				<Text type="danger">{scheduler.error}</Text>
			</SpinLoader>
			{!!userData?.user?.callEmail && (
				<Alert
					style={{ marginTop: '50px' }}
					showIcon
					type="info"
					message={<span dangerouslySetInnerHTML={{ __html: userData?.user?.callEmail }} />}
				/>
			)}
		</div>
	)
}

export default Scheduler
